// Multilingual (zh/ja/ko) mirror of the Notary FAQ hub.
// Renders A5-quality, hand-curated FAQ content with FAQPage + Speakable JSON-LD.
import { Link } from "@tanstack/react-router";
import type { NotaryFaqIntlContent } from "@/data/notary-faq-intl";
import LocaleServiceLinks from "@/components/LocaleServiceLinks";

type Props = { c: NotaryFaqIntlContent };

const HUB_LINK: Record<string, { hub: string; coverage: string; products: string }> = {
  zh: { hub: "/zh/notary", coverage: "/notary/coverage", products: "/notary/products" },
  ja: { hub: "/ja/notary", coverage: "/notary/coverage", products: "/notary/products" },
  ko: { hub: "/ko/notary", coverage: "/notary/coverage", products: "/notary/products" },
};

export default function NotaryFaqHubIntl({ c }: Props) {
  const nav = HUB_LINK[c.locale];
  return (
    <main lang={c.langAttr} className="mx-auto w-full max-w-5xl px-4 py-10 sm:py-14">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-gold">
          Notary Public · {c.locale.toUpperCase()}
        </p>
        <h1 className="mt-2 text-3xl font-extrabold leading-tight sm:text-4xl">{c.h1}</h1>
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-muted-foreground">
          {c.intro}
        </p>
        <nav className="mt-5 flex flex-wrap gap-2 text-sm">
          <Link to={nav.hub} className="rounded-full border px-3 py-1 hover:bg-muted">
            {c.hubLinkLabel}
          </Link>
          <Link to={nav.coverage} className="rounded-full border px-3 py-1 hover:bg-muted">
            {c.coverageLinkLabel}
          </Link>
          <Link to={nav.products} className="rounded-full border px-3 py-1 hover:bg-muted">
            {c.productsLinkLabel}
          </Link>
        </nav>
      </header>

      <section aria-labelledby="clusters-h" className="mb-10">
        <h2 id="clusters-h" className="mb-3 text-2xl font-bold">
          {c.clustersHeading}
        </h2>
        <p className="mb-4 max-w-3xl text-sm text-muted-foreground">{c.clusterIntro}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {c.clusters.map((cl, i) => (
            <article
              key={i}
              className="rounded-2xl border bg-card p-5 shadow-sm transition hover:shadow-md"
            >
              <h3 className="text-lg font-bold">{cl.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{cl.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section aria-labelledby="faqs-h" className="mb-10">
        <h2 id="faqs-h" className="mb-4 text-2xl font-bold">
          {c.faqsHeading}
        </h2>
        <div className="divide-y rounded-2xl border bg-card">
          {c.faqs.map((f, i) => (
            <details key={i} className="group px-5 py-4 open:bg-muted/30">
              <summary className="voice-faq-q cursor-pointer list-none text-base font-semibold marker:hidden">
                <span className="mr-2 text-primary">Q{i + 1}.</span>
                {f.q}
              </summary>
              <p
                className="voice-faq-a mt-2 text-sm leading-relaxed text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: f.a }}
              />
            </details>
          ))}
        </div>
      </section>

      <section className="mt-10 rounded-2xl border border-gold/40 bg-gradient-to-br from-navy-deep to-navy-deep/95 p-6 text-white">
        <h2 className="text-xl font-bold">{c.ctaHeading}</h2>
        <p className="mt-2 text-sm text-white/85">{c.ctaBody}</p>
      </section>
          <LocaleServiceLinks />
</main>
  );
}
